/**
 * Supervisor dropdown options for event zone forms.
 * Mirrors Admin CreateEvent StepThree / ZonesFields behavior.
 */

import { getLocalizedPersonName } from "./companyPortal/eventApi";

export const mapEventRequestToSupervisorOption = (request, lang = "en") => {
  const user = request?.user;
  const id = user?.id;
  if (id == null || id === "") return null;

  const name = getLocalizedPersonName(user, lang);

  if (!name) return null;
  return { id, name };
};

export const mapEventRequestsToSupervisorOptions = (requests, lang = "en") =>
  (Array.isArray(requests) ? requests : [])
    .map((request) => mapEventRequestToSupervisorOption(request, lang))
    .filter(Boolean);

export const mapEventZoneSupervisorOption = (zone, lang = "en") => {
  if (!zone || typeof zone !== "object") return null;

  const nested =
    zone?.supervisor && typeof zone.supervisor === "object"
      ? zone.supervisor
      : null;

  const rawId = zone?.supervisor_id ?? nested?.id;
  if (rawId == null || rawId === "") return null;

  if (nested) {
    const name = getLocalizedPersonName(nested, lang);
    if (name) return { id: rawId, name };
  }

  const fallbackName = String(zone?.supervisor_name ?? "").trim();
  if (!fallbackName) return null;
  return { id: rawId, name: fallbackName };
};

export const getSupervisorOptionsFromEventZones = (event, lang = "en") => {
  const zones = event?.zones ?? event?.zones_attributes ?? [];
  return (Array.isArray(zones) ? zones : [])
    .map((zone) => mapEventZoneSupervisorOption(zone, lang))
    .filter(Boolean);
};

export const mapFormZoneSupervisorOption = (zone) => {
  const rawId = zone?.supervisor_id;
  if (rawId == null || rawId === "") return null;
  const name = String(zone?.supervisor_name ?? "").trim() || String(rawId);
  return { id: rawId, name };
};

export const getSupervisorOptionsFromFormZones = (zones) =>
  (Array.isArray(zones) ? zones : [])
    .map(mapFormZoneSupervisorOption)
    .filter(Boolean);

/** Dedupe by user id; first non-empty name wins. */
export const mergeSupervisorOptions = (...optionLists) => {
  const byId = new Map();

  for (const list of optionLists) {
    for (const option of list || []) {
      if (!option || option.id == null || option.id === "") continue;
      const key = String(option.id);
      if (byId.has(key)) continue;
      const name = String(option.name ?? "").trim() || key;
      byId.set(key, { id: option.id, name });
    }
  }

  return Array.from(byId.values());
};

/**
 * Per-zone list: hide supervisors already picked on other zones (legacy UX).
 * Keeps the current zone's selection visible even if assigned elsewhere in form state.
 */
export const getSupervisorOptionsForZone = (allOptions, zones, zoneIndex) => {
  const currentId = zones?.[zoneIndex]?.supervisor_id;
  const currentKey =
    currentId != null && currentId !== "" ? String(currentId) : null;

  const assignedElsewhere = new Set();
  (zones || []).forEach((z, index) => {
    if (index === zoneIndex) return;
    const sid = z?.supervisor_id;
    if (sid != null && sid !== "") assignedElsewhere.add(String(sid));
  });

  return (allOptions || []).filter((option) => {
    const key = String(option.id);
    if (currentKey && key === currentKey) return true;
    return !assignedElsewhere.has(key);
  });
};
